import AppLayout from '../../components/navigation/AppLayout.jsx';
import ModuleNavigation from '../../components/navigation/ModuleNavigation.jsx';
/**
 * routes/proveedor/ProveedorApp.jsx -- Port de proveedor-login.js
 * (pantalla completa del Portal de Proveedores: login -> home). Sesión
 * manejada de forma LOCAL a este componente (useState, no
 * lib/sessionStore.js) -- misma razón deliberada que el original: ese
 * módulo persiste el remember-token bajo la llave INTERNA
 * (REMEMBER_TOKEN_KEY); si este portal la compartiera, un navegador
 * usado alguna vez para ambos podría reanudar la sesión equivocada. Este
 * portal SÍ recuerda sesión (doc 29, punto 1 de Manuel: "al actualizar me
 * deslogea") pero bajo su PROPIA llave (REMEMBER_TOKEN_KEY_PROVEEDOR) y
 * pasando siempre `portal: 'PROVEEDOR'` a login/resume -- el backend ya
 * rechaza (doc 21 §7, AUTH_PORTAL_MISMATCH) que ese token reanude una
 * cuenta interna o viceversa, así que guardarlo en localStorage es seguro.
 */
import { lazy, useEffect, useRef, useState } from 'react';
import { NavLink, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { api } from '../../lib/api.js';
import { REMEMBER_TOKEN_KEY_PROVEEDOR } from '../../lib/config.js';
import { applyProveedorTheme, applyProveedorScheme } from '../../lib/proveedorTheme.js';
import Icon from './ProveedorIcons.jsx';
import ProveedorModulesView from './ProveedorModulesView.jsx';
import { visibleProviderModules } from './moduleDefs.js';
import RouteContent from '../../components/RouteContent.jsx';
import SessionRecovery from '../../components/SessionRecovery.jsx';
import { isInvalidSavedSession } from '../../lib/sessionRecovery.js';

const ProveedorAccountView = lazy(() => import('./ProveedorAccountView.jsx'));
const ProveedorPedidosView = lazy(() => import('./ProveedorPedidosView.jsx'));
const ProveedorStatsView = lazy(() => import('./ProveedorStatsView.jsx'));
const NotFoundView = lazy(() => import('../NotFoundView.jsx'));

/** Cromo post-login: misma topbar de vidrio + pestañas segmentadas que el
 * portal interno (AppLayout.jsx), con rutas y sesión externas independientes. */
function ProveedorShell({ session, onLogout, onThemeChanged }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const modules = visibleProviderModules(session.permisos);
  const canViewBigItem = modules.some((module) => module.id === 'bigitem');
  const inBigItem = canViewBigItem && (pathname === '/pedidos' || pathname === '/pedidos/estadisticas');

  function selectModule(id) {
    if (modules.some((module) => module.id === id) && id === 'bigitem') navigate('/pedidos');
  }

  return (
    <AppLayout session={session} modules={modules} onLogout={onLogout} external>
      {inBigItem && (
        <ModuleNavigation>
          <nav className="tab-nav" aria-label="Secciones del módulo">
            <NavLink to="/pedidos" end className={({ isActive }) => `tab-btn ${isActive ? 'active' : ''}`}>
              <span className="material-symbols-rounded" aria-hidden="true">list_alt</span>Pedidos
            </NavLink>
            <NavLink to="/pedidos/estadisticas" className={({ isActive }) => `tab-btn ${isActive ? 'active' : ''}`}>
              <span className="material-symbols-rounded" aria-hidden="true">insights</span>Estadísticas
            </NavLink>
          </nav>
        </ModuleNavigation>
      )}
      <RouteContent>
        <Routes>
          <Route path="/" element={<ProveedorModulesView modules={modules} onSelect={selectModule} />} />
          <Route path="/pedidos" element={canViewBigItem ? <ProveedorPedidosView token={session.token} usuario={session.usuario} /> : <Navigate to="/" replace />} />
          <Route path="/pedidos/estadisticas" element={canViewBigItem ? <ProveedorStatsView token={session.token} /> : <Navigate to="/" replace />} />
          <Route path="/mi-cuenta" element={<ProveedorAccountView session={session} onPasswordChanged={onLogout} onThemeChanged={onThemeChanged} />} />
          <Route path="*" element={<NotFoundView />} />
        </Routes>
      </RouteContent>
    </AppLayout>
  );
}

export default function ProveedorApp() {
  const [ready, setReady] = useState(false);
  const [recoveryFailed, setRecoveryFailed] = useState(false);
  const [session, setSession] = useState(null); // { token, nombre, apellido, usuario, proveedorDescripcion }
  const [usuario, setUsuario] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Guarda contra el doble-montaje de <StrictMode> en desarrollo (React
  // 18 monta/desmonta/vuelve a montar SIEMPRE en el primer render): sin
  // esto, el bootstrap de abajo corria 2 veces en paralelo con el MISMO
  // remember-token. resumeSession() en el backend ROTA el token en cada
  // llamada (issueRememberToken), asi que la 1a resolucion lo renovaba en
  // localStorage y la 2a, todavia con el token viejo (ya invalidado),
  // fallaba y lo BORRABA -- la sesion quedaba viva en pantalla pero sin
  // nada que reanudar en el siguiente refresh real (bug de Manuel,
  // "le doy actualizar y me desloguea"). Con este ref el bootstrap corre
  // una sola vez de verdad, StrictMode o no.
  const bootstrapped = useRef(false);

  useEffect(() => {
    if (bootstrapped.current) return;
    bootstrapped.current = true;
    // Un solo bootstrap: pinta el tema Y, si hay una sesion guardada,
    // intenta reanudarla ANTES de mostrar nada -- evita un parpadeo de
    // "Iniciar sesion" seguido del home real.
    (async () => {
      await applyProveedorTheme();
      const saved = localStorage.getItem(REMEMBER_TOKEN_KEY_PROVEEDOR);
      if (saved) {
        const result = await api.resume(saved, 'PROVEEDOR');
        if (result.ok) {
          applyProveedorScheme(result.temaPreferido);
          if (result.rememberToken) localStorage.setItem(REMEMBER_TOKEN_KEY_PROVEEDOR, result.rememberToken);
          setSession(result);
        } else if (isInvalidSavedSession(result)) {
          localStorage.removeItem(REMEMBER_TOKEN_KEY_PROVEEDOR);
        } else {
          setRecoveryFailed(true);
        }
      }
      setReady(true);
    })();
  }, []);

  async function doLogout() {
    await api.logout(session.token);
    localStorage.removeItem(REMEMBER_TOKEN_KEY_PROVEEDOR);
    window.location.reload();
  }

  async function submit(event) {
    event.preventDefault();
    setError('');
    if (!usuario.trim() || !password) { setError('Captura tu usuario y contraseña.'); return; }

    setSubmitting(true);
    const result = await api.login(usuario.trim(), password, 'PROVEEDOR');
    setSubmitting(false);
    if (!result.ok) {
      // Incluye el caso "cuenta interna valida pero portal equivocado" --
      // el backend ya devuelve un mensaje claro para eso (doc 21 §7).
      setError(result.error || 'No se pudo iniciar sesión.');
      return;
    }
    applyProveedorScheme(result.temaPreferido);
    if (result.rememberToken) localStorage.setItem(REMEMBER_TOKEN_KEY_PROVEEDOR, result.rememberToken);
    setSession(result);
  }

  if (!ready) return null;

  if (recoveryFailed) return <SessionRecovery onSignIn={() => { localStorage.removeItem(REMEMBER_TOKEN_KEY_PROVEEDOR); setRecoveryFailed(false); }} />;

  if (session) return <ProveedorShell session={session} onLogout={doLogout}
    onThemeChanged={(temaPreferido) => setSession((prev) => ({ ...prev, temaPreferido }))} />;

  return (
    <section className="auth-layout">
      <div className="auth-hero" style={{ '--hero-photo': "url('/img/login-hero-proveedor.webp')" }}>
        <div className="auth-hero-content">
          <span className="auth-hero-badge">Portal de Proveedores</span>
          <h2 className="auth-hero-title">Da seguimiento a tus operaciones con Last Mile</h2>
          <p className="auth-hero-text">Centraliza tus operaciones con Last Mile en un solo lugar.</p>
          <ul className="auth-hero-points">
            <li><span className="hero-icon"><Icon name="clock" /></span>Consulta el estatus de tus pedidos</li>
            <li><span className="hero-icon"><Icon name="route" /></span>Historial de cada pedido asignado</li>
          </ul>
        </div>
      </div>

      <div className="auth-panel">
        <main id="main-content" tabIndex={-1} className="auth-card">
          <div className="auth-brand">
            <span className="auth-brand-mark" aria-hidden="true"><span className="material-symbols-rounded">local_shipping</span></span>
            <span><span className="auth-brand-name">Operaciones Last Mile 360</span><br /><span className="auth-brand-sub">Portal de Proveedores</span></span>
          </div>

          <h1 className="auth-title">Iniciar sesión</h1>
          <p className="auth-subtitle">Ingresa con tu usuario y contraseña de proveedor.</p>

          {error && <p className="auth-alert" role="alert"><Icon name="alert" /><span>{error}</span></p>}

          <form onSubmit={submit} noValidate>
            <div className="auth-field">
              <label htmlFor="login-usuario">Usuario</label>
              <div className="auth-input-wrap">
                <span className="auth-input-icon"><Icon name="user" /></span>
                <input id="login-usuario" className="auth-input" autoComplete="username" autoCapitalize="none" spellCheck={false} required value={usuario} onChange={(e) => setUsuario(e.target.value.replace(/\s/g, ''))} />
              </div>
            </div>
            <div className="auth-field">
              <label htmlFor="login-password">Contraseña</label>
              <div className="auth-input-wrap">
                <span className="auth-input-icon"><Icon name="lock" /></span>
                <input id="login-password" type="password" className="auth-input" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
              </div>
            </div>

            <button className="auth-submit" type="submit" disabled={submitting}>
              {submitting && <span className="spinner" aria-hidden="true"></span>}
              Iniciar sesión
            </button>
          </form>

          <p className="auth-register-cta">¿Aún no tienes cuenta? <a href="/registro-proveedor.html" className="auth-footer-link">Regístrate aquí</a></p>

          <div className="auth-help">
            <Icon name="info" />
            <span>Tu cuenta debe ser habilitada y vinculada a tu proveedor por un administrador antes de poder ingresar.</span>
          </div>

          <p className="auth-footer">Portal de Proveedores &middot; Uso externo</p>
        </main>
      </div>
    </section>
  );
}
